import { useState } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  FolderCog,
  FileBarChart,
  Trophy,
  Presentation,
  Plus,
  Menu,
  X,
  TrendingUp,
  ClipboardCheck,
  Activity,
  HelpCircle,
  Server,
  Library,
  BookOpen,
  Trash2,
  FileType,
  Brain,
  Cloud
} from "lucide-react";
import { Button } from "./ui/button";
import OnboardingTour from "./OnboardingTour";

const adminNavItems = [
  { path: "/admin", label: "Dashboard", icon: LayoutDashboard },
  { path: "/admin/users", label: "User Management", icon: Users },
  { path: "/admin/library", label: "Library", icon: Library },
  { path: "/admin/storage", label: "Storage Config", icon: FolderCog },
  { path: "/admin/conversion", label: "Conversion Settings", icon: FileType },
  { path: "/admin/llm", label: "LLM Configuration", icon: Brain },
  { path: "/admin/onedrive", label: "OneDrive Storage", icon: Cloud },
  { path: "/admin/reports", label: "Reports", icon: FileBarChart },
  { path: "/admin/compliance", label: "Compliance", icon: ClipboardCheck },
  { path: "/admin/activity", label: "Activity Log", icon: Activity },
  { path: "/admin/system", label: "System Status", icon: Server },
  { path: "/admin/master-delete", label: "Master Delete", icon: Trash2 },
  { path: "/admin/docs", label: "Documentation", icon: BookOpen },
];

const userNavItems = [
  { path: "/dashboard", label: "My Sessions", icon: Presentation },
  { path: "/library", label: "Library", icon: Library },
  { path: "/leaderboard", label: "Leaderboard", icon: Trophy },
];

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  const startTour = () => {
    // Force show the tour
    setShowTour(true);
  };

  const handleTourComplete = () => {
    setShowTour(false);
  };

  return (
    <div className="app-container">
      {/* Onboarding Tour */}
      <OnboardingTour onComplete={handleTourComplete} forceShow={showTour} />
      
      {/* Mobile menu button */}
      <button
        data-testid="mobile-menu-btn"
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-lg bg-white shadow-md"
        onClick={() => setSidebarOpen(!sidebarOpen)}
      >
        {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="p-6 border-b border-border">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-primary" style={{ fontFamily: 'Manrope, sans-serif' }}>
              EVOLVE
            </span>
          </Link>
        </div>

        <nav className="p-4 space-y-1 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 200px)' }}>
          <p className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Admin
          </p>
          {adminNavItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.path.replace(/\//g, '-')}`}
              className={`sidebar-nav-item ${isActive(item.path) ? 'active' : ''}`}
              onClick={() => setSidebarOpen(false)}
            >
              <item.icon size={20} />
              {item.label}
            </Link>
          ))}

          <div className="pt-4 pb-2">
            <p className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              User
            </p>
            {userNavItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.path.replace(/\//g, '-')}`}
                className={`sidebar-nav-item ${isActive(item.path) ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
              >
                <item.icon size={20} />
                {item.label}
              </Link>
            ))}
          </div>
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-border space-y-2 bg-background">
          <Link to="/session/new">
            <Button 
              data-testid="new-session-btn"
              className="w-full bg-accent hover:bg-accent/90 text-white rounded-full"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Session
            </Button>
          </Link>
          <Button 
            variant="ghost"
            onClick={startTour}
            className="w-full text-[#5C5C5C] hover:text-primary rounded-full"
          >
            <HelpCircle className="w-4 h-4 mr-2" />
            Take a Tour
          </Button>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="main-content">
        <Outlet />
      </main>
    </div>
  );
}
